import { type ReactNode, useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { usePermissions } from '../hooks/usePermissions';
import { useAuthStore } from '../stores/auth.store';
import { useFeatureFlag } from '../hooks/useFeatureFlag';
import {
  passesTenantFeatureGates,
  type TenantFeatureGate,
} from '../lib/plan-features';
import type { UserRole, TenantFeatures } from '../types/auth.types';

interface ProtectedRouteProps {
  children: ReactNode;
  /** If provided, only users with one of these roles can access this route */
  roles?: UserRole[];
  /** If provided, route is only accessible when this feature flag is enabled */
  flag?: string;
  /**
   * If provided, route is only accessible when the tenant has this feature
   * enabled (opt-out model: blocked only when explicitly set to `false`).
   * Mirrors the Sidebar's `feature` gate. NÃO use para keys default-false
   * (ex.: `whatsapp_campaigns`) — use `featureGate`.
   */
  feature?: keyof TenantFeatures;
  /**
   * Predicado OPT-IN sobre as features do tenant (ex.: `hasWhatsAppCampaigns`)
   * para keys default-false: a rota só abre quando devolve `true` (senão
   * redireciona para `/`). AND com `feature`. Espelha o `featureGate` do Sidebar.
   */
  featureGate?: TenantFeatureGate;
}

function FullScreenLoader() {
  return (
    <div className="flex items-center justify-center h-screen bg-[#FAFAFA]">
      <div className="flex flex-col items-center gap-4">
        <div className="w-12 h-12 border-4 border-brand-primary/20 border-t-brand-primary rounded-full animate-spin" />
        <p className="text-[#666666] text-sm">Carregando...</p>
      </div>
    </div>
  );
}

/**
 * Inner gate that consumes the feature-flag query. Rendered ONLY when the
 * caller passes a `flag` prop, so existing tests that don't wrap in a
 * QueryClientProvider keep working unchanged.
 */
function FeatureFlagGate({ flag, children }: { flag: string; children: ReactNode }) {
  const { enabled, isLoading } = useFeatureFlag(flag);
  if (isLoading) return <FullScreenLoader />;
  if (!enabled) return <Navigate to="/" replace />;
  return <>{children}</>;
}

/**
 * ProtectedRoute Component
 *
 * Protege rotas que requerem autenticação.
 * Redireciona para /login se não autenticado.
 * Redireciona para /dashboard se role insuficiente.
 * Quando `flag` é informado, redireciona para `/` se a flag estiver desabilitada.
 *
 * IMPORTANTE: role check e feature-flag check são AND (ambos devem passar).
 */
export function ProtectedRoute({ children, roles, flag, feature, featureGate }: ProtectedRouteProps) {
  const { isAuthenticated, isLoading, checkAuth } = useAuth();
  const { hasRole } = usePermissions();
  const location = useLocation();
  const user = useAuthStore((s) => s.user);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  if (isLoading) {
    return <FullScreenLoader />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  }

  // Block master users from tenant routes — redirect to /system
  if (user?.role === 'master' && !location.pathname.startsWith('/system')) {
    return <Navigate to="/system" replace />;
  }

  // Block org_owner from CRM routes — redirect to their organization detail page
  if (user?.role === 'org_owner' && !location.pathname.startsWith('/system')) {
    const orgTarget = user.organizationId
      ? `/system/organizations/${user.organizationId}`
      : '/system/tenants';
    return <Navigate to={orgTarget} replace />;
  }

  // Role-based access control
  if (roles && !hasRole(roles)) {
    const fallback =
      user?.role === 'master'
        ? '/system'
        : user?.role === 'org_owner'
          ? (user.organizationId ? `/system/organizations/${user.organizationId}` : '/system/tenants')
          : '/dashboard';
    return <Navigate to={fallback} replace />;
  }

  // Tenant-feature gating (AND with role/flag), igual à Sidebar: `feature` é
  // opt-out (bloqueia só com `=== false`) e `featureGate` é o predicado opt-in
  // das keys default-false. Tenant sem a feature `automations` → /fluxos
  // redireciona p/ home (o link já está oculto na nav).
  if (
    !passesTenantFeatureGates({ feature, featureGate }, user?.tenantFeatures)
  ) {
    return <Navigate to="/" replace />;
  }

  // Feature-flag gating (AND with role check above). Only mounted when needed
  // so non-gated routes don't require a QueryClientProvider in tests.
  if (flag) {
    return <FeatureFlagGate flag={flag}>{children}</FeatureFlagGate>;
  }

  return <>{children}</>;
}
